import { useUserPreferencesStore, useWorkplaceStore } from '@host/stores';
import { Logger } from '@/shared/services/logger.service';
import {
  resolveNotificationBucket,
  type ClientNotificationChannels,
  type NotificationTypeKey,
} from './notification-bucket.util';

const NONE: ClientNotificationChannels = { app: false, browser: false };

/**
 * Whether the real workspace preferences have arrived from the server.
 *
 * Callers must not alert (sound or bubble) before this is true: until the fetch resolves
 * the store holds opt-in placeholders that are all `false`, so evaluating against them
 * silently discards notifications. Queue instead, and flush via onPreferencesReady().
 */
export function arePreferencesReady(): boolean {
  return useUserPreferencesStore.getState().workspaceLoaded === true;
}

/** Invoke `cb` once preferences are ready — immediately if they already are. */
export function onPreferencesReady(cb: () => void): () => void {
  if (arePreferencesReady()) {
    cb();
    return () => {};
  }

  // `fired` guards against re-entry, and declaring `unsubscribe` up front avoids a temporal
  // dead zone if a store implementation ever invoked the listener during subscribe().
  let fired = false;
  let unsubscribe: () => void = () => {};

  unsubscribe = useUserPreferencesStore.subscribe((state) => {
    if (fired || !state.workspaceLoaded) return;
    fired = true;
    unsubscribe();
    cb();
  });

  return unsubscribe;
}

/**
 * Which channels the client should render for a notification.
 *
 * Prefers the server-resolved value carried on the socket payload: the backend owns the
 * preference matrix and the status→bucket decision, and the client simply obeying it is
 * what stops the two from disagreeing — the root cause behind NGE-988, where the frontend
 * treated `meeting` as Active while the backend treated it as Away.
 *
 * Falls back to resolving locally when the payload carries no `channels` field, so a
 * webapp deploy landing before the events service still behaves correctly.
 */
export function resolveClientChannels(
  type: NotificationTypeKey,
  serverResolved?: Partial<ClientNotificationChannels> | null
): ClientNotificationChannels {
  if (
    serverResolved &&
    typeof serverResolved.app === 'boolean' &&
    typeof serverResolved.browser === 'boolean'
  ) {
    return { app: serverResolved.app, browser: serverResolved.browser };
  }
  return resolveLocally(type);
}

function resolveLocally(type: NotificationTypeKey): ClientNotificationChannels {
  if (!arePreferencesReady()) return NONE;

  const notifications =
    useUserPreferencesStore.getState().preferences?.workspace?.settings?.notifications;

  // Distinguish "the user switched this off" (normal, silent) from "we have no preferences at
  // all" (broken — the fetch failed, so every channel resolves to false and the user gets
  // nothing). The latter is logged at error level because it is indistinguishable from
  // working-as-configured from the outside, and it is exactly how notifications go quiet
  // without anyone knowing why.
  if (!notifications) {
    Logger.error(
      `[NotificationChannels] No saved preferences available — suppressing all channels for ` +
        `"${type}". Workspace preferences failed to load; reload to retry.`
    );
    return NONE;
  }

  // Receiving a notification over the socket proves the socket is connected, so the
  // offline bucket can never apply on the client — only Active vs Away is in question.
  const bucket = resolveNotificationBucket(useWorkplaceStore.getState().getUserStatus(), true);
  const row = (notifications as Record<string, Record<string, Record<string, boolean>>>)[bucket]?.[
    type
  ];

  // Explicit === true, never `?? true`: an absent key means "not enabled", matching the
  // opt-in default the settings UI and backend both use (see PR #1368).
  return { app: row?.app === true, browser: row?.browser === true };
}
